import { sipAccountFromBuildEnv, sipDomainHostFromEnv, sipWebSocketUrlFromEnv, } from './sipAccount.defaults';
const DEFAULT_SIP_DOMAIN_HOST = 'vcgateway.parsa.technology';
const DEFAULT_SIP_WS_URL = 'wss://vcgateway.parsa.technology/ws';
/** Values like `0783919922` are contact numbers, not SIP hosts. */
function isLikelyPhoneNumber(value) {
    const trimmed = value.trim();
    if (!trimmed || trimmed.includes('.') || /^https?:\/\//i.test(trimmed) || /^wss?:\/\//i.test(trimmed)) {
        return false;
    }
    const digits = trimmed.replace(/\D/g, '');
    return digits.length >= 10 && !trimmed.includes('/');
}
function isLikelyHostOrUrl(value) {
    const trimmed = value.trim();
    if (!trimmed || isLikelyPhoneNumber(trimmed)) {
        return false;
    }
    if (/^wss?:\/\//i.test(trimmed) || /^https?:\/\//i.test(trimmed)) {
        return true;
    }
    return /^[\w.-]+(:\d+)?(\/.*)?$/i.test(trimmed);
}
/**
 * Normalize CRM `phoneIp` (+ optional `phonePort`) → sip.js WebSocket URL (`wss://…`).
 * Accepts full URLs, bare hostnames, or host/path without scheme.
 */
export function normalizeSipWebSocketUrl(phoneIp, phonePort) {
    const trimmed = phoneIp.trim();
    if (!trimmed)
        return '';
    if (/^wss?:\/\//i.test(trimmed)) {
        return trimmed.replace(/\s+/g, '');
    }
    if (/^https?:\/\//i.test(trimmed)) {
        try {
            const url = new URL(trimmed);
            const wsProto = url.protocol === 'https:' ? 'wss:' : 'ws:';
            return `${wsProto}//${url.host}${url.pathname}${url.search}`.replace(/\s+/g, '');
        }
        catch {
            return '';
        }
    }
    if (!isLikelyHostOrUrl(trimmed)) {
        return '';
    }
    const port = phonePort?.trim() ?? '';
    const slashIdx = trimmed.indexOf('/');
    const hostPart = slashIdx >= 0 ? trimmed.slice(0, slashIdx) : trimmed;
    const pathPart = slashIdx >= 0 ? trimmed.slice(slashIdx) : '/ws';
    let hostWithPort = hostPart;
    if (!hostPart.includes(':') && port && port !== '443') {
        hostWithPort = `${hostPart}:${port}`;
    }
    const useTls = !port || port === '443';
    const wsProto = useTls ? 'wss:' : 'ws:';
    const normalizedPath = pathPart.startsWith('/') ? pathPart : `/${pathPart}`;
    return `${wsProto}//${hostWithPort}${normalizedPath}`.replace(/\s+/g, '');
}
/** WebSocket URL from profile fields, falling back to build-time `SIP_WS_URL` when `phoneIp` is unset or invalid. */
export function resolveSipWebSocketUrl(profile) {
    const phoneIp = profile.phoneIp?.trim() ?? '';
    const phonePort = profile.phonePort?.trim() ?? '';
    if (phoneIp) {
        const fromProfile = normalizeSipWebSocketUrl(phoneIp, phonePort);
        if (fromProfile) {
            return fromProfile;
        }
    }
    return sipWebSocketUrlFromEnv(DEFAULT_SIP_WS_URL).trim();
}
function hostFromWebSocketUrl(wsUrl) {
    try {
        return new URL(wsUrl).hostname;
    }
    catch {
        return sipDomainHostFromEnv(DEFAULT_SIP_DOMAIN_HOST);
    }
}
/** Build sip.js account config from CRM profile phone fields (complete or partial). */
export function sipAccountFromCrmProfile(profile) {
    const extension = profile.phoneUser?.trim() ?? '';
    if (!extension) {
        return null;
    }
    const password = profile.phonePassword?.trim() ?? '';
    const sipWebSocketServerUrl = resolveSipWebSocketUrl(profile);
    const sipDomainHost = sipWebSocketServerUrl
        ? hostFromWebSocketUrl(sipWebSocketServerUrl)
        : sipDomainHostFromEnv(DEFAULT_SIP_DOMAIN_HOST);
    return {
        accountName: extension,
        callerId: extension,
        extension,
        authId: extension,
        password,
        sipDomainHost,
        sipWebSocketServerUrl,
    };
}
/** Placeholder when CRM profile has no softphone fields and no `.env` SIP override. */
export const UNASSIGNED_SIP_ACCOUNT = {
    accountName: 'unknown',
    callerId: 'unknown',
    extension: 'unknown',
    authId: '',
    password: '',
    sipDomainHost: sipDomainHostFromEnv(DEFAULT_SIP_DOMAIN_HOST),
    sipWebSocketServerUrl: '',
};
export function isSipAccountConfigured(acc) {
    return Boolean(acc.extension &&
        acc.extension !== 'unknown' &&
        acc.authId &&
        acc.password &&
        acc.sipWebSocketServerUrl);
}
export function sipAccountConfigError(acc) {
    if (!acc.extension || acc.extension === 'unknown') {
        return 'Softphone extension not assigned in your profile.';
    }
    if (!acc.sipWebSocketServerUrl) {
        return 'Softphone WebSocket URL not configured in your profile.';
    }
    if (!acc.password) {
        return 'SIP password not configured in your profile.';
    }
    return null;
}
/**
 * Profile from `/api/users/profile` when `phoneUser` is set.
 * Static `.env` credentials are only used when no CRM profile extension exists (local dev).
 */
export function resolveSipAccount(profile, options) {
    const profileLoaded = options?.profileLoaded ?? true;
    if (profile?.phoneUser?.trim()) {
        const fromProfile = sipAccountFromCrmProfile(profile);
        if (fromProfile) {
            return fromProfile;
        }
    }
    if (!profileLoaded) {
        return UNASSIGNED_SIP_ACCOUNT;
    }
    const fromEnv = sipAccountFromBuildEnv();
    if (fromEnv) {
        return fromEnv;
    }
    return UNASSIGNED_SIP_ACCOUNT;
}
